import { Helper } from '../Common/Helpers/helper';
import { Injectable } from '@angular/core';
import { plainToClass } from 'class-transformer';
import { UnitConfigApiModel } from '../Services/api/v2/Models/UnitConfigApiModel';
import { SpaEventService } from '../Services/spaEvents.service';
import { GuiConfigType } from '../Constants/GuiConfigType';
import { UnitModel } from '../Services/api/v2/Models/UnitModel';
import { Controls } from '../Constants/Controls';
import { GeneralService } from '../Services/general.service';
import { TranslateService } from '../Common/Services/translate.service';
import { LocationSelectorService } from '@services/location_selector.service';
import { ConfigService } from '@services/config.service';
import { sharedResolution } from '../../../src/shared_resolution';

@Injectable()
export class SpaContext {
	private _unitConfig: UnitConfigApiModel = null;
	get unitConfig() {
		return this._unitConfig;
	}

	documentSigningPending = false;
	languageCode: string = null;
	sessionStorage: Storage = window.sessionStorage;
	unitTreeFlat: Array<UnitModel> = [];
	rootUnitId: number;

	private readonly locationSelectorService = sharedResolution.resolve(LocationSelectorService);
	private readonly configService = sharedResolution.resolve(ConfigService);

	get guiConfig() {
		return this.getCurrentConfig();
	}

	get baseHref() {
		return `/${this.locationSelectorService.location.route}/`;
	}

	constructor(
		private _spaEventService: SpaEventService,
		private _helper: Helper,
		private generalService: GeneralService,
		private translateService: TranslateService,
	) {
		// Empty
	}

	init(unitConfig: UnitConfigApiModel, unitTree: UnitModel) {
		this.setUnitConfig(unitConfig);
		this.setUnitTree(unitTree);

		const langCode = localStorage.getItem('languageCode');
		this.languageCode = langCode ? langCode : µ.Env.LangCode;

		this._spaEventService.onSigningIn.subscribe(() => {
			const langCode = localStorage.getItem('languageCode');
			if (langCode) {
				this.generalService.setlanguage(langCode).subscribe();
			}
		});

		// Workaround for syncing SpaContext and LocationSelectorService
		this.locationSelectorService.events.on('onCommitted', (unitId: number) => {
			const loadedConfig = this.configService.__TEMP__loadedConfigsRaw.get(unitId);
			if (loadedConfig == null) throw new Error(`No config has been set for unit with id ${unitId}`);

			const config = {
				...loadedConfig,

				// About isn't a part of the API model and is set at startup separately in StartupService.
				// The initial one set at startup is fetched for the license holder.
				// This always sets a reference to the initial one, since it doesn't change between units.
				About: this.unitConfig.About,
				WebContentButtons: [],
			};

			this.setUnitConfig(config);
		});
	}

	setUnitConfig(data: UnitConfigApiModel) {
		this._unitConfig = plainToClass(UnitConfigApiModel, data);
		this.translateService.setTranslations(this._unitConfig.Translations);

		if (this._unitConfig.Offer == null) {
			this._unitConfig.Offer = {
				// Please better solution one day
				HasConditionWorkflow: false,
				AllowClientAgents: false,
				HideProvidedByVisibaCare: false,
				HasPhysicalLocation: false,
				Controls: [],
			};
		}
	}

	setUnitTree(root: UnitModel) {
		this.rootUnitId = root.Id;
		this.unitTreeFlat = this._helper.unit.createFlatUnitArray(root);
		const validNodeIds = [root.Id];

		// Detach branches with no Offer from root
		const endNodesWithOffer = this.unitTreeFlat.filter(
			(u) => (u.IsClientEntrance || u.closestEntranceId !== null) && u.Children.length === 0,
		);
		endNodesWithOffer.forEach((n) => {
			let node = n;
			while (node.ParentId !== null) {
				validNodeIds.push(node.Id);
				node = this.unitTreeFlat.find((u) => u.Id === node.ParentId);
			}
		});

		this.unitTreeFlat.forEach((u) => (u.Children = u.Children.filter((c) => validNodeIds.some((v) => v === c.Id))));
	}

	findUnit(searchForId: number): UnitModel {
		return this.unitTreeFlat.find((unit) => unit.Id === searchForId);
	}

	hasControl(name: Controls) {
		return this.unitConfig.Offer.Controls.indexOf(name) > -1;
	}

	identifiers(countryCode = null) {
		if (countryCode === null) countryCode = this.unitConfig.Locale.DefaultCountry;

		const supportedCountry = this.unitConfig.Locale.SupportedCountries.find((c) => c.CountryCode === countryCode);

		return {
			phone: supportedCountry.ClientIdentifierPhoneNumber,
			nin: supportedCountry.ClientIdentifierNationalIdentityNumber,
		};
	}

	setGuiConfigItem(item: string | GuiConfigType, value: any) {
		const config = this.getCurrentConfig();
		config[item] = value;

		window.localStorage.setItem(`HolderConfig_${this.unitConfig.LicenceHolder.Id}`, JSON.stringify(config));
	}

	/**
	 * To get typed
	 * @param item
	 */
	getGuiConfigItem<T>(item: string | GuiConfigType): T | null {
		const config = this.getCurrentConfig();

		return config[item] || null;
	}

	private getCurrentConfig(): { [name: string]: any } {
		let currentConfig: any = window.localStorage.getItem(`HolderConfig_${this.unitConfig.LicenceHolder.Id}`);
		if (currentConfig === null) currentConfig = {};
		else currentConfig = JSON.parse(currentConfig);

		return currentConfig;
	}
}
